/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { ERROR_MESSAGES } from '../../constants'
import type { ChatCompletionChunk } from '../../types'

const STREAM_DONE_MESSAGE = '[DONE]'
const STREAM_CLOSED_READY_STATE = 2

export type StreamUpdateType = 'reasoning' | 'content'

export type StreamMessageUpdate = {
  type: StreamUpdateType
  chunk: string
}

type StreamErrorPayload = {
  error?: {
    code?: string
    message?: string
  }
}

export type StreamErrorDetails = {
  errorCode?: string
  errorMessage: string
}

export type SseEvent = { event: string; data: string }

export function parseSseEvents(input: string): {
  events: SseEvent[]
  rest: string
} {
  const events: SseEvent[] = []
  const separator = /\r\n\r\n|\n\n|\r\r/g
  let consumed = 0
  let match: RegExpExecArray | null

  while ((match = separator.exec(input)) !== null) {
    const lines = input.slice(consumed, match.index).split(/\r\n|\n|\r/)
    const data: string[] = []
    let event = 'message'

    for (const line of lines) {
      if (line.startsWith('data:')) {
        data.push(line.slice(5).replace(/^ /, ''))
      } else if (line.startsWith('event:')) {
        event = line.slice(6).trim()
      }
    }

    if (data.length > 0) events.push({ event, data: data.join('\n') })
    consumed = separator.lastIndex
  }

  return { events, rest: input.slice(consumed) }
}

export function parseStreamErrorMessage(
  data: string
): StreamErrorDetails | null {
  try {
    const parsed = JSON.parse(data) as StreamErrorPayload
    return parsed?.error ? parseStreamErrorDetails(data) : null
  } catch {
    return null
  }
}

export function parseStreamErrorDetails(data?: string): StreamErrorDetails {
  const fallbackMessage =
    data && !data.trimStart().startsWith('<')
      ? data.slice(0, 500)
      : ERROR_MESSAGES.API_REQUEST_ERROR

  if (!data) {
    return { errorMessage: fallbackMessage }
  }

  try {
    const parsed = JSON.parse(data) as StreamErrorPayload

    if (!parsed?.error) {
      return { errorMessage: fallbackMessage }
    }

    return {
      errorCode: parsed.error.code || undefined,
      errorMessage: parsed.error.message || fallbackMessage,
    }
  } catch {
    return { errorMessage: fallbackMessage }
  }
}

export function parseStreamMessageUpdates(data: string): StreamMessageUpdate[] {
  const chunk = JSON.parse(data) as ChatCompletionChunk
  const delta = chunk.choices?.[0]?.delta

  if (!delta) {
    return []
  }

  const updates: StreamMessageUpdate[] = []

  if (delta.reasoning_content) {
    updates.push({ type: 'reasoning', chunk: delta.reasoning_content })
  }

  if (delta.content) {
    updates.push({ type: 'content', chunk: delta.content })
  }

  return updates
}

export function isStreamDoneMessage(data: string): boolean {
  return data === STREAM_DONE_MESSAGE
}

export function isStreamClosedReadyState(readyState?: number): boolean {
  return readyState === STREAM_CLOSED_READY_STATE
}

export function getStreamReadyStateError(
  eventReadyState: number | undefined,
  source: unknown
): string | null {
  const status = (source as { status?: number }).status

  if (
    eventReadyState !== undefined &&
    eventReadyState >= STREAM_CLOSED_READY_STATE &&
    status !== undefined &&
    status !== 200
  ) {
    return `HTTP ${status}: ${ERROR_MESSAGES.CONNECTION_CLOSED}`
  }

  return null
}
